import { buildBrightnessContrastLut } from "../filters/BrightnessContrast";
import { buildLevelsLut } from "../filters/Levels";
import { buildCurvesLut } from "../filters/Curves";
import type { Lut3D } from "../filters/ColorLookup";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AdjustmentParamValue = number | string | boolean | null | Record<string, any> | AdjustmentParamValue[];

export interface AdjustmentParams {
  kind: string;
  params: Record<string, AdjustmentParamValue>;
}

export function applyAdjustment(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  params: AdjustmentParams,
): void {
  const n = width * height;
  switch (params.kind) {
    case "brightness-contrast": {
      const brightness = (params.params.brightness ?? 0) as number;
      const contrast = (params.params.contrast ?? 0) as number;
      const lut = buildBrightnessContrastLut({ brightness, contrast });
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pixels[o] = lut[pixels[o] ?? 0] ?? 0;
        pixels[o + 1] = lut[pixels[o + 1] ?? 0] ?? 0;
        pixels[o + 2] = lut[pixels[o + 2] ?? 0] ?? 0;
      }
      break;
    }
    case "levels": {
      const channel = (params.params.channel ?? "rgb") as string;
      const inputBlack = (params.params.inputBlack ?? 0) as number;
      const inputWhite = (params.params.inputWhite ?? 255) as number;
      const gamma = (params.params.gamma ?? 1) as number;
      const outputBlack = (params.params.outputBlack ?? 0) as number;
      const outputWhite = (params.params.outputWhite ?? 255) as number;
      const lut = buildLevelsLut({ channel: channel as any, inputBlack, inputWhite, gamma, outputBlack, outputWhite });
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pixels[o] = lut[pixels[o] ?? 0] ?? 0;
        pixels[o + 1] = lut[pixels[o + 1] ?? 0] ?? 0;
        pixels[o + 2] = lut[pixels[o + 2] ?? 0] ?? 0;
      }
      break;
    }
    case "curves": {
      const pointsRaw = params.params.points as string | { input: number; output: number }[] | undefined;
      let points: { input: number; output: number }[] = [];
      if (typeof pointsRaw === "string") {
        try { points = JSON.parse(pointsRaw); } catch { /* use default */ }
      } else if (Array.isArray(pointsRaw)) {
        points = pointsRaw;
      }
      if (points.length === 0) {
        points = [{ input: 0, output: 0 }, { input: 255, output: 255 }];
      }
      const lut = buildCurvesLut(points);
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pixels[o] = lut[pixels[o] ?? 0] ?? 0;
        pixels[o + 1] = lut[pixels[o + 1] ?? 0] ?? 0;
        pixels[o + 2] = lut[pixels[o + 2] ?? 0] ?? 0;
      }
      break;
    }
    case "hue-saturation": {
      applyHueSaturationInPlace(pixels, n, params);
      break;
    }
    case "color-balance": {
      applyColorBalanceInPlace(pixels, n, params);
      break;
    }
    case "black-white": {
      applyBlackWhiteInPlace(pixels, n, params);
      break;
    }
    case "photo-filter": {
      applyPhotoFilterInPlace(pixels, n, params);
      break;
    }
    case "gradient-map": {
      applyGradientMapInPlace(pixels, n, params);
      break;
    }
    case "invert": {
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pixels[o] = 255 - (pixels[o] ?? 0);
        pixels[o + 1] = 255 - (pixels[o + 1] ?? 0);
        pixels[o + 2] = 255 - (pixels[o + 2] ?? 0);
      }
      break;
    }
    case "posterize": {
      const levels = Math.max(2, Math.min(255, (params.params.levels ?? 4) as number));
      const step = 255 / (levels - 1);
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pixels[o] = Math.round(Math.round((pixels[o] ?? 0) / step) * step);
        pixels[o + 1] = Math.round(Math.round((pixels[o + 1] ?? 0) / step) * step);
        pixels[o + 2] = Math.round(Math.round((pixels[o + 2] ?? 0) / step) * step);
      }
      break;
    }
    case "threshold": {
      const level = Math.max(0, Math.min(255, (params.params.level ?? 128) as number));
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        const r = pixels[o] ?? 0;
        const g = pixels[o + 1] ?? 0;
        const b = pixels[o + 2] ?? 0;
        const lum = (r * 77 + g * 150 + b * 29) >> 8;
        const val = lum <= level ? 0 : 255;
        pixels[o] = val;
        pixels[o + 1] = val;
        pixels[o + 2] = val;
      }
      break;
    }
    case "solid-color": {
      const color = params.params.color as { r: number; g: number; b: number } | undefined;
      if (!color) break;
      const opacity = ((params.params.opacity ?? 100) as number) / 100;
      const cr = color.r;
      const cg = color.g;
      const cb = color.b;
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pixels[o] = Math.round((pixels[o] ?? 0) * (1 - opacity) + cr * opacity);
        pixels[o + 1] = Math.round((pixels[o + 1] ?? 0) * (1 - opacity) + cg * opacity);
        pixels[o + 2] = Math.round((pixels[o + 2] ?? 0) * (1 - opacity) + cb * opacity);
      }
      break;
    }
    case "exposure": {
      const exposure = clampRange(params.params.exposure as number, -20, 20);
      const offset = clampRange(params.params.offset as number, -0.5, 0.5);
      const gamma = clampRange(params.params.gamma as number, 0.01, 9.99);
      const exposureScale = Math.pow(2, exposure);
      const invGamma = 1 / gamma;
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        const a = pixels[o + 3] ?? 0;
        if (a === 0) continue;
        const vr = ((pixels[o] ?? 0) / 255) * exposureScale + offset;
        const vg = ((pixels[o + 1] ?? 0) / 255) * exposureScale + offset;
        const vb = ((pixels[o + 2] ?? 0) / 255) * exposureScale + offset;
        pixels[o] = Math.round(clamp01(Math.pow(vr, invGamma)) * 255);
        pixels[o + 1] = Math.round(clamp01(Math.pow(vg, invGamma)) * 255);
        pixels[o + 2] = Math.round(clamp01(Math.pow(vb, invGamma)) * 255);
      }
      break;
    }
    case "vibrance": {
      applyVibranceInPlace(pixels, n, params);
      break;
    }
    case "channel-mixer": {
      applyChannelMixerInPlace(pixels, n, params);
      break;
    }
    case "selective-color": {
      applySelectiveColorInPlace(pixels, n, params);
      break;
    }
    case "color-lookup": {
      applyColorLookupInPlace(pixels, n, params);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Hue/Saturation in-place
// ---------------------------------------------------------------------------

function applyHueSaturationInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const hueShift = clampRange(params.params.hue as number, -180, 180);
  const satDelta = clampRange(params.params.saturation as number, -100, 100);
  const ligDelta = clampRange(params.params.lightness as number, -100, 100);
  const colorize = !!params.params.colorize;
  const colHue = (((params.params.colorizeHue ?? 0) as number % 360) + 360) % 360;
  const colSat = clampRange(params.params.colorizeSat as number, 0, 100) / 100;

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    let { h, s, l } = rgbToHsl(r, g, b);

    if (colorize) {
      h = colHue;
      s = colSat;
    } else {
      if (hueShift !== 0) h = ((h + hueShift) % 360 + 360) % 360;
      if (satDelta !== 0) s = clamp01(s * (1 + satDelta / 100));
    }
    if (ligDelta !== 0) l = clamp01(adjustLightness(l, ligDelta));

    const [ro, go, bo] = hslToRgb({ h, s, l });
    pixels[o] = ro;
    pixels[o + 1] = go;
    pixels[o + 2] = bo;
  }
}

// ---------------------------------------------------------------------------
// Color Balance in-place
// ---------------------------------------------------------------------------

function applyColorBalanceInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const shadows = {
    cyanRed: clampRange(params.params.shadowsCyanRed as number, -100, 100),
    magentaGreen: clampRange(params.params.shadowsMagentaGreen as number, -100, 100),
    yellowBlue: clampRange(params.params.shadowsYellowBlue as number, -100, 100),
  };
  const midtones = {
    cyanRed: clampRange(params.params.midtonesCyanRed as number, -100, 100),
    magentaGreen: clampRange(params.params.midtonesMagentaGreen as number, -100, 100),
    yellowBlue: clampRange(params.params.midtonesYellowBlue as number, -100, 100),
  };
  const highlights = {
    cyanRed: clampRange(params.params.highlightsCyanRed as number, -100, 100),
    magentaGreen: clampRange(params.params.highlightsMagentaGreen as number, -100, 100),
    yellowBlue: clampRange(params.params.highlightsYellowBlue as number, -100, 100),
  };
  const preserve = !!params.params.preserveLuminosity;

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    const lum = 0.3 * r + 0.59 * g + 0.11 * b;
    const tone = lum < 85 ? shadows : lum > 170 ? highlights : midtones;

    let ro = r + (tone.cyanRed / 100) * 255;
    let go = g + (tone.magentaGreen / 100) * 255;
    let bo = b + (tone.yellowBlue / 100) * 255;
    ro = clampByte(Math.round(ro));
    go = clampByte(Math.round(go));
    bo = clampByte(Math.round(bo));

    if (preserve) {
      const origLum = lum;
      const outLum = 0.3 * ro + 0.59 * go + 0.11 * bo;
      if (outLum > 0) {
        const scale = origLum / outLum;
        ro = clampByte(Math.round(ro * scale));
        go = clampByte(Math.round(go * scale));
        bo = clampByte(Math.round(bo * scale));
      }
    }

    pixels[o] = ro;
    pixels[o + 1] = go;
    pixels[o + 2] = bo;
  }
}

// ---------------------------------------------------------------------------
// Black & White in-place
// ---------------------------------------------------------------------------

function applyBlackWhiteInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const weights = [
    sliderWeight(clampRange(params.params.reds as number, -200, 300)),
    sliderWeight(clampRange(params.params.yellows as number, -200, 300)),
    sliderWeight(clampRange(params.params.greens as number, -200, 300)),
    sliderWeight(clampRange(params.params.cyans as number, -200, 300)),
    sliderWeight(clampRange(params.params.blues as number, -200, 300)),
    sliderWeight(clampRange(params.params.magentas as number, -200, 300)),
  ];
  const tint = !!params.params.tint;
  const tintAmount = clampRange(params.params.tintAmount as number, 0, 100) / 100;
  const tintColor = params.params.tintColor as { r: number; g: number; b: number } | undefined;

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    let gray: number;
    if (r === g && g === b) {
      gray = r;
    } else {
      const hue = rgbToHue(r, g, b);
      const weight = interpolateWeight(hue, weights);
      const luma = 0.299 * r + 0.587 * g + 0.114 * b;
      gray = clampRange(luma * weight, 0, 255);
    }

    if (tint && tintAmount > 0 && tintColor) {
      const inv = 1 - tintAmount;
      pixels[o] = clampRange(gray * inv + tintColor.r * tintAmount, 0, 255);
      pixels[o + 1] = clampRange(gray * inv + tintColor.g * tintAmount, 0, 255);
      pixels[o + 2] = clampRange(gray * inv + tintColor.b * tintAmount, 0, 255);
    } else {
      pixels[o] = clampRange(gray, 0, 255);
      pixels[o + 1] = clampRange(gray, 0, 255);
      pixels[o + 2] = clampRange(gray, 0, 255);
    }
  }
}

// ---------------------------------------------------------------------------
// Photo Filter in-place
// ---------------------------------------------------------------------------

function applyPhotoFilterInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const density = clampRange(params.params.density as number, 0, 100);
  const filterFactor = density / 100;
  const preserveLum = !!params.params.preserveLuminosity;
  const color = params.params.color as { r: number; g: number; b: number } | undefined;
  if (!color) return;
  const { r: fr, g: fg, b: fb } = color;

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    let outR = r * (1 - filterFactor) + fr * filterFactor;
    let outG = g * (1 - filterFactor) + fg * filterFactor;
    let outB = b * (1 - filterFactor) + fb * filterFactor;

    if (preserveLum) {
      const origLum = 0.3 * r + 0.59 * g + 0.11 * b;
      const outLum = 0.3 * outR + 0.59 * outG + 0.11 * outB;
      if (outLum > 0) {
        const scale = origLum / outLum;
        outR = clamp01(outR * scale) * 255;
        outG = clamp01(outG * scale) * 255;
        outB = clamp01(outB * scale) * 255;
      } else {
        outR = 0; outG = 0; outB = 0;
      }
    }

    pixels[o] = Math.round(outR);
    pixels[o + 1] = Math.round(outG);
    pixels[o + 2] = Math.round(outB);
  }
}

// ---------------------------------------------------------------------------
// Gradient Map in-place
// ---------------------------------------------------------------------------

function applyGradientMapInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const stops = params.params.stops as { position: number; color: { r: number; g: number; b: number } }[] | undefined;
  if (!stops || stops.length < 2) return;
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const rev = !!params.params.reverse;

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    let t = (r * 77 + g * 150 + b * 29) / (255 * 256);
    t = clamp01(t);
    if (rev) t = 1 - t;

    const col = sampleGradient(sorted, t);
    pixels[o] = col.r;
    pixels[o + 1] = col.g;
    pixels[o + 2] = col.b;
  }
}

// ---------------------------------------------------------------------------
// Vibrance in-place
// ---------------------------------------------------------------------------

function applyVibranceInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const vibrance = clampRange(params.params.vibrance as number, -100, 100);
  const satDelta = clampRange(params.params.saturation as number, -100, 100);

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    let hsl = rgbToHsl(r, g, b);

    if (vibrance !== 0) {
      let satAdjust: number;
      if (vibrance > 0) {
        satAdjust = vibrance * (1 - hsl.s) * (1 - hsl.s);
      } else {
        satAdjust = vibrance * hsl.s;
      }
      hsl.s = clamp01(hsl.s + satAdjust / 100);
    }

    if (satDelta !== 0) {
      if (satDelta > 0) {
        hsl.s = hsl.s + (satDelta / 100) * (1 - hsl.s);
      } else {
        hsl.s = hsl.s + (satDelta / 100) * hsl.s;
      }
      hsl.s = clamp01(hsl.s);
    }

    const [ro, go, bo] = hslToRgb(hsl);
    pixels[o] = ro;
    pixels[o + 1] = go;
    pixels[o + 2] = bo;
  }
}

// ---------------------------------------------------------------------------
// Channel Mixer in-place
// ---------------------------------------------------------------------------

function applyChannelMixerInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  // Params may arrive as the structured ChannelMixerParams or as a flat
  // serialised object (from adjustmentDialogs defaults). Support both.
  const p = params.params as Record<string, unknown>;

  // Read per-channel mixer settings, falling back to identity values.
  const rmRed   = ((p["red.red"]   ?? (p.red as Record<string,number> | undefined)?.red   ?? 100) as number);
  const rmGreen = ((p["red.green"] ?? (p.red as Record<string,number> | undefined)?.green ?? 0)   as number);
  const rmBlue  = ((p["red.blue"]  ?? (p.red as Record<string,number> | undefined)?.blue  ?? 0)   as number);
  const rmConst = ((p["red.constant"] ?? (p.red as Record<string,number> | undefined)?.constant ?? 0) as number);

  const gmRed   = ((p["green.red"]   ?? (p.green as Record<string,number> | undefined)?.red   ?? 0)   as number);
  const gmGreen = ((p["green.green"] ?? (p.green as Record<string,number> | undefined)?.green ?? 100) as number);
  const gmBlue  = ((p["green.blue"]  ?? (p.green as Record<string,number> | undefined)?.blue  ?? 0)   as number);
  const gmConst = ((p["green.constant"] ?? (p.green as Record<string,number> | undefined)?.constant ?? 0) as number);

  const bmRed   = ((p["blue.red"]   ?? (p.blue as Record<string,number> | undefined)?.red   ?? 0)   as number);
  const bmGreen = ((p["blue.green"] ?? (p.blue as Record<string,number> | undefined)?.green ?? 0)   as number);
  const bmBlue  = ((p["blue.blue"]  ?? (p.blue as Record<string,number> | undefined)?.blue  ?? 100) as number);
  const bmConst = ((p["blue.constant"] ?? (p.blue as Record<string,number> | undefined)?.constant ?? 0) as number);

  const monochrome = !!(p.monochrome);

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    if (monochrome) {
      const gray = clampByte(
        r * (rmRed / 100) +
        g * (rmGreen / 100) +
        b * (rmBlue / 100) +
        rmConst,
      );
      pixels[o] = gray;
      pixels[o + 1] = gray;
      pixels[o + 2] = gray;
    } else {
      pixels[o] = clampByte(
        r * (rmRed / 100) + g * (rmGreen / 100) + b * (rmBlue / 100) + rmConst,
      );
      pixels[o + 1] = clampByte(
        r * (gmRed / 100) + g * (gmGreen / 100) + b * (gmBlue / 100) + gmConst,
      );
      pixels[o + 2] = clampByte(
        r * (bmRed / 100) + g * (bmGreen / 100) + b * (bmBlue / 100) + bmConst,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Selective Color in-place
// ---------------------------------------------------------------------------

function getSelectiveColorWeights(r: number, g: number, b: number): Record<string, number> {
  const lum = (r * 0.3 + g * 0.59 + b * 0.11) / 255;
  return {
    reds:     r > g && r > b ? (r - Math.max(g, b)) / 255 : 0,
    yellows:  r > b && g > b ? Math.min(r, g) / 255 - b / 255 : 0,
    greens:   g > r && g > b ? (g - Math.max(r, b)) / 255 : 0,
    cyans:    g > r && b > r ? Math.min(g, b) / 255 - r / 255 : 0,
    blues:    b > r && b > g ? (b - Math.max(r, g)) / 255 : 0,
    magentas: r > g && b > g ? Math.min(r, b) / 255 - g / 255 : 0,
    whites:   lum > 0.5 ? (lum - 0.5) * 2 : 0,
    neutrals: lum > 0.25 && lum < 0.75 ? 1 - Math.abs(lum - 0.5) * 4 : 0,
    blacks:   lum < 0.5 ? (0.5 - lum) * 2 : 0,
  };
}

const RANGE_KEYS = ["reds", "yellows", "greens", "cyans", "blues", "magentas", "whites", "neutrals", "blacks"] as const;

function getRangeParam(p: Record<string, unknown>, range: string, channel: string): number {
  // Support both nested {reds: {cyan: N}} and flat {"reds.cyan": N} layouts.
  const nested = (p[range] as Record<string, number> | undefined)?.[channel];
  if (nested !== undefined) return nested as number;
  const flat = p[`${range}.${channel}`];
  if (flat !== undefined) return flat as number;
  return 0;
}

function clampSelectiveRange(v: number): number {
  if (!isFinite(v)) return 0;
  return v < -100 ? -100 : v > 100 ? 100 : v;
}

function applySelectiveColorInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const p = params.params as Record<string, unknown>;
  const method = (p.method as string | undefined) ?? "relative";

  // Pre-read all range params once.
  const rangeParams: Record<string, { c: number; m: number; y: number; k: number }> = {};
  for (const range of RANGE_KEYS) {
    rangeParams[range] = {
      c: clampSelectiveRange(getRangeParam(p, range, "cyan")),
      m: clampSelectiveRange(getRangeParam(p, range, "magenta")),
      y: clampSelectiveRange(getRangeParam(p, range, "yellow")),
      k: clampSelectiveRange(getRangeParam(p, range, "black")),
    };
  }

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    // RGB → CMYK
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const kk = 1 - Math.max(rn, gn, bn);
    let c: number, m: number, y: number, k: number;
    if (kk >= 1) {
      c = 0; m = 0; y = 0; k = 1;
    } else {
      const denom = 1 - kk;
      c = (1 - rn - kk) / denom;
      m = (1 - gn - kk) / denom;
      y = (1 - bn - kk) / denom;
      k = kk;
    }

    const weights = getSelectiveColorWeights(r, g, b);

    for (const range of RANGE_KEYS) {
      const wgt = weights[range] ?? 0;
      if (wgt <= 0) continue;
      const rp = rangeParams[range]!;
      if (method === "relative") {
        c += (rp.c / 100) * c * wgt;
        m += (rp.m / 100) * m * wgt;
        y += (rp.y / 100) * y * wgt;
        k += (rp.k / 100) * k * wgt;
      } else {
        c += (rp.c / 100) * wgt;
        m += (rp.m / 100) * wgt;
        y += (rp.y / 100) * wgt;
        k += (rp.k / 100) * wgt;
      }
    }

    c = c < 0 ? 0 : c > 1 ? 1 : c;
    m = m < 0 ? 0 : m > 1 ? 1 : m;
    y = y < 0 ? 0 : y > 1 ? 1 : y;
    k = k < 0 ? 0 : k > 1 ? 1 : k;

    pixels[o]     = clampByte(Math.round(255 * (1 - c) * (1 - k)));
    pixels[o + 1] = clampByte(Math.round(255 * (1 - m) * (1 - k)));
    pixels[o + 2] = clampByte(Math.round(255 * (1 - y) * (1 - k)));
  }
}

// ---------------------------------------------------------------------------
// Color Lookup (3D LUT) in-place
// ---------------------------------------------------------------------------

function applyColorLookupInPlace(
  pixels: Uint8ClampedArray,
  n: number,
  params: AdjustmentParams,
): void {
  const p = params.params as Record<string, unknown>;
  const lutParam = p.lut as Lut3D | null | undefined;
  if (!lutParam) return; // no LUT → identity pass-through

  const size = lutParam.size;
  if (!size || size < 2) return;

  // Accept either Float32Array or plain number[].
  const data: { [idx: number]: number } = lutParam.data as unknown as { [idx: number]: number };
  const s = size - 1;
  const opacity = clamp01(typeof p.opacity === "number" ? p.opacity : 1);

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = pixels[o] ?? 0;
    const g = pixels[o + 1] ?? 0;
    const b = pixels[o + 2] ?? 0;
    const a = pixels[o + 3] ?? 0;
    if (a === 0) continue;

    const rf = (r / 255) * s;
    const gf = (g / 255) * s;
    const bf = (b / 255) * s;

    const ri = Math.floor(rf);
    const gi = Math.floor(gf);
    const bi = Math.floor(bf);
    const rfrac = rf - ri;
    const gfrac = gf - gi;
    const bfrac = bf - bi;

    const ri1 = ri + 1 < size ? ri + 1 : s;
    const gi1 = gi + 1 < size ? gi + 1 : s;
    const bi1 = bi + 1 < size ? bi + 1 : s;

    // LUT index: blue outermost, green middle, red innermost (cube format).
    const idx = (rr: number, gg: number, bb: number) => (bb * size * size + gg * size + rr) * 3;

    const i000 = idx(ri, gi, bi);
    const i100 = idx(ri1, gi, bi);
    const i010 = idx(ri, gi1, bi);
    const i110 = idx(ri1, gi1, bi);
    const i001 = idx(ri, gi, bi1);
    const i101 = idx(ri1, gi, bi1);
    const i011 = idx(ri, gi1, bi1);
    const i111 = idx(ri1, gi1, bi1);

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    const r00 = lerp(data[i000]!, data[i100]!, rfrac);
    const r10 = lerp(data[i010]!, data[i110]!, rfrac);
    const r01 = lerp(data[i001]!, data[i101]!, rfrac);
    const r11 = lerp(data[i011]!, data[i111]!, rfrac);
    const r0  = lerp(r00, r10, gfrac);
    const r1  = lerp(r01, r11, gfrac);
    let rout  = lerp(r0, r1, bfrac);

    const g00 = lerp(data[i000 + 1]!, data[i100 + 1]!, rfrac);
    const g10 = lerp(data[i010 + 1]!, data[i110 + 1]!, rfrac);
    const g01 = lerp(data[i001 + 1]!, data[i101 + 1]!, rfrac);
    const g11 = lerp(data[i011 + 1]!, data[i111 + 1]!, rfrac);
    const g0  = lerp(g00, g10, gfrac);
    const g1  = lerp(g01, g11, gfrac);
    let gout  = lerp(g0, g1, bfrac);

    const b00 = lerp(data[i000 + 2]!, data[i100 + 2]!, rfrac);
    const b10 = lerp(data[i010 + 2]!, data[i110 + 2]!, rfrac);
    const b01 = lerp(data[i001 + 2]!, data[i101 + 2]!, rfrac);
    const b11 = lerp(data[i011 + 2]!, data[i111 + 2]!, rfrac);
    const b0  = lerp(b00, b10, gfrac);
    const b1  = lerp(b01, b11, gfrac);
    let bout  = lerp(b0, b1, bfrac);

    if (opacity < 1) {
      rout = lerp(r, rout, opacity);
      gout = lerp(g, gout, opacity);
      bout = lerp(b, bout, opacity);
    }

    pixels[o]     = clampByte(rout);
    pixels[o + 1] = clampByte(gout);
    pixels[o + 2] = clampByte(bout);
  }
}

// ---------------------------------------------------------------------------
// HSL helpers (shared across adjustments)
// ---------------------------------------------------------------------------

interface HSL { h: number; s: number; l: number; }

function rgbToHsl(r: number, g: number, b: number): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  else if (max === gn) h = ((bn - rn) / d + 2) * 60;
  else h = ((rn - gn) / d + 4) * 60;
  return { h, s, l };
}

function hslToRgb(hsl: HSL): [number, number, number] {
  const { h, s, l } = hsl;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0, g1 = 0, b1 = 0;
  if (hue < 60) { r1 = c; g1 = x; b1 = 0; }
  else if (hue < 120) { r1 = x; g1 = c; b1 = 0; }
  else if (hue < 180) { r1 = 0; g1 = c; b1 = x; }
  else if (hue < 240) { r1 = 0; g1 = x; b1 = c; }
  else if (hue < 300) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }
  return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)];
}

function adjustLightness(l: number, lightPct: number): number {
  if (lightPct === 0) return l;
  if (lightPct > 0) { const factor = lightPct / 100; return l + factor * (1 - l); }
  const factor = -lightPct / 100;
  return l - factor * l;
}

function rgbToHue(r: number, g: number, b: number): number {
  const rn = r / 255; const gn = g / 255; const bn = b / 255;
  const max = Math.max(rn, gn, bn); const min = Math.min(rn, gn, bn);
  if (max === min) return 0;
  const d = max - min;
  let hue: number;
  if (max === rn) hue = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  else if (max === gn) hue = ((bn - rn) / d + 2) * 60;
  else hue = ((rn - gn) / d + 4) * 60;
  return ((hue % 360) + 360) % 360;
}

function sliderWeight(v: number): number { return Math.max(0, (v + 100) / 100); }

function interpolateWeight(hue: number, w: number[]): number {
  const segmentAngle = ((hue % 360) + 360) % 360 / 60;
  const segIdx = Math.floor(segmentAngle) % 6;
  const t = segmentAngle - Math.floor(segmentAngle);
  const nextIdx = (segIdx + 1) % 6;
  return w[segIdx]! * (1 - t) + w[nextIdx]! * t;
}

function sampleGradient(stops: { position: number; color: { r: number; g: number; b: number } }[], t: number): { r: number; g: number; b: number } {
  if (t <= stops[0]!.position) return stops[0]!.color;
  if (t >= stops[stops.length - 1]!.position) return stops[stops.length - 1]!.color;
  for (let i = 0; i < stops.length - 1; i++) {
    const lo = stops[i]!; const hi = stops[i + 1]!;
    if (t >= lo.position && t <= hi.position) {
      const span = hi.position - lo.position;
      const f = span === 0 ? 0 : (t - lo.position) / span;
      return {
        r: Math.round(lo.color.r + (hi.color.r - lo.color.r) * f),
        g: Math.round(lo.color.g + (hi.color.g - lo.color.g) * f),
        b: Math.round(lo.color.b + (hi.color.b - lo.color.b) * f),
      };
    }
  }
  return stops[stops.length - 1]!.color;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }

function clampByte(v: number): number { return v < 0 ? 0 : v > 255 ? 255 : v; }
